import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GeoSetTargetable } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import type { ITanStackTableProps } from '@feathr/components';
import { Button, EmptyState, Icon, TanStackTable } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import geoFiltersColumns from './geoFiltersColumns';
import { applyNameSort, getCampaignCountsByGeoSetId, sortGeoSets } from './GeoFiltersTable.utils';

type TSort = NonNullable<ITanStackTableProps<GeoSetTargetable>['sort']>;

const DEFAULT_PAGE_SIZE = 10;

interface IGeoFiltersTableProps {
  defaultGeoSetId?: string;
  onCreateSet: () => void;
  onEditSet: (targetable: GeoSetTargetable) => void;
  onSetDefault: (targetable: GeoSetTargetable) => void;
}

function GeoFiltersTable({
  defaultGeoSetId,
  onCreateSet,
  onEditSet,
  onSetDefault,
}: Readonly<IGeoFiltersTableProps>): JSX.Element {
  const { t } = useTranslation();
  const { Targetables, Targetings } = useStore();
  const [sort, setSort] = useState<TSort>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const geoSetTargetables = Targetables.list({
    filters: { is_archived__ne: true, _cls: TargetableClass.geo_set },
  });

  const geoSetTargetings = Targetings.list({
    filters: { kind: 'geo_set', is_archived__ne: true },
  });

  const campaignCountsByGeoSetId = getCampaignCountsByGeoSetId(geoSetTargetings.models);

  if (geoSetTargetables.isLoaded && geoSetTargetables.models.length === 0) {
    return (
      <EmptyState
        description={t('Get started by creating a new set.')}
        height={'full'}
        label={t('No geofilters')}
        theme={'white'}
        width={'full'}
      >
        <Button onClick={onCreateSet} prefix={<Icon icon={faPlus} />}>
          {t('Create set')}
        </Button>
      </EmptyState>
    );
  }

  /*
   * TanStackTable is manual sort + manual pagination: it renders exactly the rows it's
   * given. To preserve the pre-migration client-side behavior (no extra requests), sort
   * (default set first, then alpha) and slice the already-fetched list here, then hand the
   * table the current page's rows plus controlled sort/page state.
   */
  const models = geoSetTargetables.models as unknown as GeoSetTargetable[];
  const sortedModels = applyNameSort(sortGeoSets(models, defaultGeoSetId), sort);

  const pageCount = Math.max(1, Math.ceil(sortedModels.length / pageSize));
  const clampedPage = Math.min(page, pageCount - 1);
  const pageRows = sortedModels.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize);

  const columns = geoFiltersColumns(
    t,
    onEditSet,
    onSetDefault,
    defaultGeoSetId,
    campaignCountsByGeoSetId,
  );

  function handleSortChange(nextSort: TSort): void {
    setSort(nextSort);
    setPage(0);
  }

  function handlePageSizeChange(nextPageSize: number): void {
    setPageSize(nextPageSize);
    setPage(0);
  }

  return (
    <TanStackTable<GeoSetTargetable>
      columns={columns}
      idKey={'id'}
      isLoading={geoSetTargetables.isPending}
      items={pageRows}
      onPageChange={setPage}
      onPageSizeChange={handlePageSizeChange}
      onSortChange={handleSortChange}
      page={clampedPage}
      pages={pageCount}
      pageSize={pageSize}
      sort={sort}
    />
  );
}

export default observer(GeoFiltersTable);
